//#region Util Imports

import * as moment from 'moment'
import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import 'devextreme/ui/load_indicator'

//#endregion

import { Enums } from '../../../models/common/enums'

import { OpTaskProtocolVerification } from '../../../models/custom/op-task-protocol-verification'

import { GridOptions, GridTypes, GridController } from '../../common/grid/grid.controller'
import { TableController } from '../../common/common.table.controller'
import { LocalizationHelper } from '../../../helpers/localization-helper'
import { CommonHelper, TypeHelper } from '../../../helpers'
import { ConfigHelper } from '../../../helpers/config-helper'

import { IMRBaseControlController } from '../../common/base'
import { IIMRTaskTaskVerificationHistory, IMRTTaskTaskVerificationHistoryOptions } from './task-verification-history.interfaces'
import { UserSessionService } from '../../../services/user-session'
import { GridService } from '../../../services/grid'

export class IMRTaskVerificationHistoryController extends IMRBaseControlController<IIMRTaskTaskVerificationHistory, IMRTTaskTaskVerificationHistoryOptions> implements IIMRTaskTaskVerificationHistory {
	// #region Consts

	private get EventDateName(): string {
		return 'eventDate'
	}
	private get DescrName(): string {
		return 'descr'
	}
	private get StatusName(): string {
		return 'status'
	}

	//#endregion

	//#region members

	public showGrid: boolean
	public gridOptions: GridOptions

	private grid: GridController<any>

	//#endregion

	//#region Ctor

	constructor(private $timeout: ng.ITimeoutService, private readonly userSessionService: UserSessionService) {
		super()
	}

	//#endregion

	//#region Component

	protected initialize(options: IMRTTaskTaskVerificationHistoryOptions): void {
		this.showGrid = false

		this.onClear()

		setTimeout(() => {
			this.setOptions()
		})
	}

	//#endregion

	//#region IIMRDepositoryParameters implementation

	//#endregion

	//#region LifeCycle

	private onClear(): void {
		this.gridOptions = null
	}

	private getTaskProtocolToVerification(idTask: number): JQueryPromise<OpTaskProtocolVerification[]> {
		return this.userSessionService
			.getDataProvider()
			.Task.getTaskProtocolToVerification(idTask)
			.then((protocols: OpTaskProtocolVerification[]) => {
				return protocols
			})
	}

	private setOptions() {
		const dateTimeColumn: DevExpress.ui.dxDataGridColumn = this.getDateTimeColumn()

		// for datetime only
		TableController.addCustomFiltersToDxColumn(dateTimeColumn, 'date', { type: Enums.DataTypeClass.Datetime }, false, null, null, null)

		this.gridOptions = {
			name: 'TaskVerificationHistory' + this.options.referenceValue,
			idReferenceType: Enums.ReferenceType.None,
			type: GridTypes.PAGINATED_SMALL,
			columns: this.getHistoryGridColumns(dateTimeColumn),
			getDataSource: () => this.getTaskProtocolToVerification(this.options.referenceValue),
			dataSourceParser: (dataItem: OpTaskProtocolVerification) => this.getDataSourceparcer(dataItem),
			masterDetail: {
				enabled: false,
				template: 'detail',
				autoExpandAll: true,
			},
			onContentReady: (e) => {
				this.onContentReady(e)
			},
			onRowPrepared: (e) => {
				this.onRowPrepared(e)
			},
			onRowDoubleClick: (e) => this.onRowDoubleClick(e),
			onInitialized: (e: any, grid: GridController<any>) => {
				this.grid = grid

				if (_.isFunction(this.options.onInitialized)) {
					this.options.onInitialized(grid)
				}
			},
			buttonsOptions: { enabled: true },
			searchVisibleColumnsOnly: false,
			addCustomFilters: true,
			allowColumnReordering: false,
		}

		if (this.options.dataSource != null) {
			this.gridOptions.dataSource = this.options.dataSource
			this.gridOptions.getDataSource = null
		}
	}

	private onRowPrepared(e: any): void {
		if (e.rowElement.hasClass('dx-master-detail-row') && !e.data.longDescr) e.rowElement.addClass('display-none')

		//for showing tooltip
		if (e.rowType == 'detail') {
			if (_.size(e.rowElement) > 0) {
				const td: HTMLElement = e.rowElement[0].lastChild

				if (td != null) {
					td.setAttribute('title', e.data.longDescr)
				}
			}
		}
	}

	private onContentReady(e: any): void {
		this.showGrid = true
	}

	private getDateTimeColumn(): any {
		return {
			dataField: this.EventDateName,
			dataType: 'date',
			caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Date),
			customizeText: (options) => {
				return moment(options.value).format(ConfigHelper.getDateFormat(ConfigHelper.DateFormat.DATE_FORMAT_MOMENTJS))
			},
			editorOptions: TableController.getColumnEditorOptions('0', Enums.DataTypeClass.Datetime, this.$timeout),
			visible: true,
			columnType: Enums.DataTypeClass.Datetime,
		}
	}

	private getHistoryGridColumns(dateTimeColumn: any): DevExpress.ui.dxDataGridColumn[] {
		const columns: DevExpress.ui.dxDataGridColumn[] = [
			dateTimeColumn,
			<DevExpress.ui.dxDataGridColumn>{ dataField: this.DescrName, dataType: 'string', caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description), visible: true },
			<DevExpress.ui.dxDataGridColumn>{ dataField: this.StatusName, dataType: 'string', caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Status), visible: true },
		]

		return columns
	}

	private onRowDoubleClick(row: any): void {
		if (_.isFunction(this.options.onRowDoubleClick)) {
			const sourceObjectJSON: string = row.data[GridService.SOURCE_OBJECT_FIELD_NAME]

			if (!TypeHelper.isNullOrEmpty(sourceObjectJSON)) {
				this.options.onRowDoubleClick(new OpTaskProtocolVerification(sourceObjectJSON))
			} else {
				this.options.onRowDoubleClick(row.data)
			}
		}
	}

	private getDataSourceparcer(dataItem: OpTaskProtocolVerification): any {
		if (dataItem == null) {
			return null
		}

		const additionalFieldsObj: { additionalFields: { key: string; value: any }[] } = {
			additionalFields: [],
		}

		additionalFieldsObj.additionalFields.push({
			key: this.EventDateName,
			value: CommonHelper.cellFormatForDifferentValues(this.userSessionService, { value: dataItem.timestamp, typeId: Enums.DataTypeClass.Datetime }, null, null),
		})

		additionalFieldsObj.additionalFields.push({
			key: this.DescrName,
			value: dataItem.toString(),
		})

		additionalFieldsObj.additionalFields.push({
			key: this.StatusName,
			value: dataItem.statusDescription,
		})

		return additionalFieldsObj
	}

	//#endregion
}
